import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Mail, KeyRound, CheckCircle2, ArrowLeft } from 'lucide-react';
import { requestPasswordReset } from '@/lib/api';
import { AuthLayout } from '@/components/AuthLayout';
import formStyles from '@/components/AuthForm.module.css';

const schema = z.object({
  email: z.string().email('Enter a valid email'),
});
type FormValues = z.infer<typeof schema>;

interface Props {
  onNavigate: (path: string) => void;
}

/**
 * Admin forgot-password REQUEST form (AAP.3). Sibling of PWA's
 * ForgotPasswordPage and structural twin of admin's MagicLinkPage
 * — same shape, different submit target.
 *
 * Submit path: requestPasswordReset(email) → POST /auth/forgot-password
 * with { email, origin: 'admin' }. AAP.1's caller-determined host
 * routing templates the reset link against ADMIN_ORIGIN.
 *
 * After submit, the form is replaced by a success state with the
 * deliberately-ambiguous "if that email is registered..." copy —
 * the request endpoint always returns success regardless of
 * whether the address matches a real user, so the UI must not
 * leak existence either.
 */
export function ForgotPasswordPage({ onNavigate }: Props) {
  const [sent, setSent] = useState(false);
  const [serverError, setServerError] = useState('');
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const onSubmit = async (values: FormValues) => {
    setServerError('');
    try {
      await requestPasswordReset(values.email);
      setSent(true);
    } catch (err) {
      setServerError(err instanceof Error ? err.message : 'Request failed');
    }
  };

  return (
    <AuthLayout
      title="Reset your password"
      subtitle="We'll email you a link to set a new password."
      footer={
        <a
          href="/login"
          onClick={(e) => {
            e.preventDefault();
            onNavigate('/login');
          }}
        >
          <ArrowLeft size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
          Back to sign in
        </a>
      }
    >
      {sent ? (
        <div className={formStyles.formSuccess} role="status">
          <CheckCircle2 size={16} style={{ verticalAlign: 'middle', marginRight: 8 }} />
          If that email is registered, a reset link has been sent.
          Check your inbox. The link expires in 1 hour.
        </div>
      ) : (
        <form className={formStyles.form} onSubmit={handleSubmit(onSubmit)} noValidate>
          {serverError && (
            <div className={formStyles.serverError} role="alert">
              {serverError}
            </div>
          )}
          <div className={formStyles.field}>
            <label htmlFor="email" className={formStyles.label}>
              <Mail size={12} style={{ verticalAlign: 'middle', marginRight: 6 }} />
              Email
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              placeholder="admin@evinitiative.com"
              className={`${formStyles.input} ${errors.email ? formStyles.inputError : ''}`}
              {...register('email')}
            />
            {errors.email && (
              <p className={formStyles.error}>{errors.email.message}</p>
            )}
          </div>
          <button
            type="submit"
            className={formStyles.submitBtn}
            disabled={isSubmitting}
          >
            <KeyRound size={14} aria-hidden style={{ marginRight: 6 }} />
            {isSubmitting ? 'Sending…' : 'Email me a reset link'}
          </button>
        </form>
      )}
    </AuthLayout>
  );
}
